import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  X, ChevronRight, ChevronLeft, CheckCircle2, Building2,
  Users, Cpu, Clock, Send, AlertTriangle, Plus, Loader2,
  Stethoscope, ShieldCheck, ArrowRight, Zap, CalendarDays, Trash2,
} from 'lucide-react';

/* ──────────── MOCK DATA ──────────── */
const PROVIDERS = [
  { id: 'p1', nome: 'Coop. Sanitas', zona: 'Distretto Roma 2', punteggio: 92, specialita: 'Riabilitazione', tempoRisposta: '~2h' },
  { id: 'p2', nome: 'Assistenza Domani Srl', zona: 'Roma Sud', punteggio: 88, specialita: 'Cure Palliative', tempoRisposta: '~4h' },
  { id: 'p3', nome: 'CasaCura Plus', zona: 'Distretto Roma 1', punteggio: 85, specialita: 'Infermieristica Avanzata', tempoRisposta: '~3h' },
  { id: 'p4', nome: 'ServiziSalute Coop', zona: 'Roma Est', punteggio: 78, specialita: 'Assistenza Base', tempoRisposta: '~6h' },
  { id: 'p5', nome: 'Progetto Domicilio', zona: 'Distretto Roma 3', punteggio: 82, specialita: 'Polispecialistica', tempoRisposta: '~5h' },
];

const OPERATORI_ASL = [
  { id: 'o1', nome: 'Inf. Rosa Colombo', ruolo: 'Infermiere', carico: 'basso', percentuale: 35 },
  { id: 'o2', nome: 'Ft. Marco Galli', ruolo: 'Fisioterapista', carico: 'medio', percentuale: 65 },
  { id: 'o3', nome: 'OSS Anna Ferretti', ruolo: 'OSS', carico: 'alto', percentuale: 90 },
  { id: 'o4', nome: 'Inf. Luca Bianchi', ruolo: 'Infermiere', carico: 'basso', percentuale: 25 },
  { id: 'o5', nome: 'Ft. Elena Ricci', ruolo: 'Fisioterapista', carico: 'basso', percentuale: 30 },
  { id: 'o6', nome: 'OSS Paolo Neri', ruolo: 'OSS', carico: 'medio', percentuale: 55 },
];

const FABBISOGNO = [
  { tipo: 'Infermieristica', ruolo: 'Infermiere', accessi: 3, descrizione: 'Monitoraggio parametri e medicazioni' },
  { tipo: 'Fisioterapia', ruolo: 'Fisioterapista', accessi: 5, descrizione: 'Riabilitazione motoria' },
  { tipo: 'Assistenza OSS', ruolo: 'OSS', accessi: 4, descrizione: 'Igiene personale e supporto ADL' },
];

const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven'];
const SLOT_ORARI = ['08:00', '09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];

/* Mock: ~35% degli slot risultano occupati per operatore */
const OCCUPIED_SLOTS = {
  o1: ['Lun-09:00','Lun-14:00','Mar-08:00','Mar-11:00','Mer-10:00','Mer-15:00','Gio-08:00','Gio-16:00','Ven-09:00','Ven-14:00'],
  o2: ['Lun-08:00','Lun-10:00','Lun-15:00','Mar-09:00','Mar-14:00','Mer-08:00','Mer-11:00','Gio-10:00','Gio-15:00','Ven-08:00','Ven-16:00'],
  o3: ['Lun-08:00','Lun-09:00','Lun-10:00','Lun-14:00','Mar-08:00','Mar-09:00','Mar-14:00','Mar-15:00','Mer-08:00','Mer-09:00','Mer-14:00','Gio-08:00','Gio-09:00','Gio-14:00','Ven-08:00','Ven-09:00'],
  o4: ['Lun-11:00','Mar-10:00','Mer-09:00','Mer-16:00','Gio-11:00','Ven-10:00','Ven-15:00'],
  o5: ['Lun-08:00','Mar-14:00','Mar-16:00','Mer-11:00','Gio-09:00','Gio-14:00','Ven-11:00','Ven-16:00'],
  o6: ['Lun-09:00','Lun-14:00','Lun-16:00','Mar-08:00','Mar-10:00','Mer-08:00','Mer-14:00','Mer-15:00','Gio-08:00','Gio-16:00','Ven-09:00','Ven-14:00'],
};

function isSlotOccupied(opId, giorno, ora) {
  const key = `${giorno}-${ora}`;
  return (OCCUPIED_SLOTS[opId] || []).includes(key);
}

/* ──────────── HELPERS ──────────── */
function formatCountdown(seconds) {
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/* ──────────── STEP 1: SCELTA + VALIDAZIONE ──────────── */
function Step1({ patient, validated, setValidated, gestioneTipo, setGestioneTipo, onNext }) {
  const canProceed = validated && gestioneTipo;

  return (
    <div className="flex flex-col gap-6" data-testid="assegnazione-step1">
      {/* Riepilogo PAI */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Riepilogo PAI</p>
        <Card className="border border-border/60 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Diagnosi principale</p>
            <p className="text-sm font-semibold text-foreground mb-4">{patient.diagnosi}</p>
            <p className="text-xs text-muted-foreground mb-2">Prestazioni richieste</p>
            <div className="flex flex-col gap-2.5">
              {FABBISOGNO.map((f) => (
                <div key={f.tipo} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/30">
                  <div className="flex items-center gap-2.5">
                    <Stethoscope className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-xs font-semibold">{f.tipo}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{f.accessi} accessi/sett.</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Biforcazione — PRIMA della checkbox */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Modalita di gestione</p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { key: 'esterna', icon: Building2, title: 'Gestione Esterna', desc: 'Provider Accreditati' },
            { key: 'interna', icon: Users, title: 'Gestione Interna', desc: 'Team ASL' },
          ].map(({ key, icon: Icon, title, desc }) => (
            <button
              key={key}
              onClick={() => setGestioneTipo(key)}
              className={`p-5 rounded-xl border-2 text-left transition-all ${
                gestioneTipo === key
                  ? 'border-violet-400 bg-violet-50/60 dark:bg-violet-950/30 shadow-sm'
                  : 'border-border/40 hover:border-border hover:bg-muted/20'
              }`}
              data-testid={`gestione-${key}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${gestioneTipo === key ? 'bg-violet-100 dark:bg-violet-900/40' : 'bg-muted/40'}`}>
                <Icon className={`w-5 h-5 ${gestioneTipo === key ? 'text-violet-600 dark:text-violet-400' : 'text-muted-foreground'}`} />
              </div>
              <p className="text-sm font-semibold text-foreground">{title}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Checkbox validazione — DOPO le card di scelta */}
      <label
        className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${validated ? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border/40 hover:border-border'}`}
        data-testid="validazione-checkbox"
      >
        <input
          type="checkbox"
          checked={validated}
          onChange={(e) => setValidated(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-emerald-500"
        />
        <div>
          <p className="text-sm font-semibold text-foreground">Confermo la validazione clinica del PAI</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Dichiaro che il Piano Assistenziale e stato rivisto e approvato.</p>
        </div>
      </label>

      {/* Avanti */}
      <Button
        disabled={!canProceed}
        onClick={onNext}
        className="w-full h-12 rounded-xl gap-2 bg-violet-600 hover:bg-violet-700 text-white"
        data-testid="step1-avanti"
      >
        Avanti <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

/* ──────────── STEP 2A: GESTIONE ESTERNA ──────────── */
function Step2Esterna({ onConfirm, onBack }) {
  const [selected, setSelected] = useState([]);
  const [invioEffettuato, setInvioEffettuato] = useState(false);
  const [countdown, setCountdown] = useState(86399);

  useEffect(() => {
    if (!invioEffettuato) return;
    const timer = setInterval(() => setCountdown((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(timer);
  }, [invioEffettuato]);

  const toggleProvider = (pid) => {
    setSelected((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length >= 3) return prev;
      return [...prev, pid];
    });
  };

  /* Job Rotation: salta direttamente al cruscotto con 3 provider pre-selezionati */
  const handleJobRotation = useCallback(() => {
    const sorted = [...PROVIDERS].sort((a, b) => b.punteggio - a.punteggio);
    setSelected(sorted.slice(0, 3).map((p) => p.id));
    setInvioEffettuato(true);
  }, []);

  const handleInvio = () => setInvioEffettuato(true);

  return (
    <div className="flex flex-col gap-6" data-testid="assegnazione-step2a">
      {!invioEffettuato ? (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Seleziona fino a 3 Provider</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{selected.length}/3 selezionati</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleJobRotation}
              className="gap-2 rounded-lg text-xs"
              data-testid="ai-job-rotation"
            >
              <Cpu className="w-3.5 h-3.5" />
              Job Rotation
            </Button>
          </div>

          {/* Provider list */}
          <div className="flex flex-col gap-3">
            {PROVIDERS.map((prov) => {
              const isSelected = selected.includes(prov.id);
              return (
                <button
                  key={prov.id}
                  onClick={() => toggleProvider(prov.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected ? 'border-violet-400 bg-violet-50/50 dark:bg-violet-950/30' : 'border-border/40 hover:border-border'
                  }`}
                  data-testid={`provider-${prov.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${isSelected ? 'border-violet-500 bg-violet-500' : 'border-muted-foreground/30'}`}>
                        {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{prov.nome}</p>
                        <p className="text-[10px] text-muted-foreground">{prov.zona} - {prov.specialita}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-violet-600 dark:text-violet-400">{prov.punteggio}<span className="text-[10px] font-normal text-muted-foreground">/100</span></p>
                      <p className="text-[10px] text-muted-foreground">{prov.tempoRisposta}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Invia */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onBack} className="flex-1 h-11 rounded-xl gap-2" data-testid="step2a-indietro">
              <ChevronLeft className="w-4 h-4" /> Indietro
            </Button>
            <Button
              disabled={selected.length === 0}
              onClick={handleInvio}
              className="flex-1 h-11 rounded-xl gap-2 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="step2a-invia"
            >
              <Send className="w-4 h-4" /> Invia Richiesta
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Cruscotto Invio */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-amber-500" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cruscotto Invio</p>
            </div>

            <div className="flex flex-col gap-3">
              {selected.map((pid) => {
                const prov = PROVIDERS.find((p) => p.id === pid);
                return (
                  <div key={pid} className="flex items-center justify-between p-4 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800" data-testid={`tracking-${pid}`}>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{prov.nome}</p>
                      <p className="text-[10px] text-muted-foreground">{prov.zona}</p>
                    </div>
                    <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-0 text-[10px] font-semibold gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> In attesa
                    </Badge>
                  </div>
                );
              })}
            </div>

            {/* Countdown */}
            <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-border/40 text-center">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Tempo rimanente</p>
              <p className="text-2xl font-bold text-foreground font-mono tracking-widest" data-testid="countdown-timer">{formatCountdown(countdown)}</p>
            </div>

            {/* Fallback */}
            <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900">
              <AlertTriangle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-blue-700 dark:text-blue-400 leading-relaxed">
                Se il timer scade, la richiesta verra inoltrata in broadcast a tutti i provider del distretto (First-come, First-served).
              </p>
            </div>
          </div>

          {/* Conferma finale */}
          <Button
            onClick={onConfirm}
            className="w-full h-12 rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="conferma-assegnazione"
          >
            <CheckCircle2 className="w-4 h-4" /> Conferma Assegnazione
          </Button>
        </>
      )}
    </div>
  );
}

/* ──────────── STEP 2B: GESTIONE INTERNA (CALENDARIZZAZIONE) ──────────── */
function Step2Interna({ onConfirm, onBack }) {
  const [assegnazioni, setAssegnazioni] = useState({});
  const [appuntamenti, setAppuntamenti] = useState({});

  const handleSelectOperatore = (tipo, opId) => {
    setAssegnazioni((prev) => ({ ...prev, [tipo]: opId }));
  };

  const addSlot = (tipo, giorno, ora) => {
    setAppuntamenti((prev) => {
      const current = prev[tipo] || [];
      const exists = current.some((s) => s.giorno === giorno && s.ora === ora);
      if (exists) return prev;
      return { ...prev, [tipo]: [...current, { giorno, ora }] };
    });
  };

  const removeSlot = (tipo, giorno, ora) => {
    setAppuntamenti((prev) => {
      const current = prev[tipo] || [];
      return { ...prev, [tipo]: current.filter((s) => !(s.giorno === giorno && s.ora === ora)) };
    });
  };

  const totalSlots = Object.values(appuntamenti).reduce((acc, arr) => acc + arr.length, 0);
  const totalAssigned = Object.keys(assegnazioni).length;

  return (
    <div className="flex flex-col gap-6" data-testid="assegnazione-step2b">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-violet-500" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pianificazione Settimanale per Fabbisogno</p>
      </div>

      {FABBISOGNO.map((fab, idx) => {
        const operatoriDisponibili = OPERATORI_ASL.filter((o) => o.ruolo === fab.ruolo);
        const selectedOp = assegnazioni[fab.tipo] || '';
        const slots = appuntamenti[fab.tipo] || [];

        return (
          <Card key={fab.tipo} className="border border-border/60 shadow-none overflow-hidden" data-testid={`fabbisogno-${idx}`}>
            <CardContent className="p-0">
              {/* Header fabbisogno */}
              <div className="flex items-center justify-between p-4 bg-violet-50/60 dark:bg-violet-950/20 border-b border-border/40">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-xs font-bold text-violet-700 dark:text-violet-400">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{fab.tipo}</p>
                    <p className="text-[10px] text-muted-foreground">{fab.descrizione}</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px]">{fab.accessi} acc./sett.</Badge>
              </div>

              <div className="p-4 flex flex-col gap-4">
                {/* Select professionista */}
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 block">Professionista assegnato</label>
                  <select
                    value={selectedOp}
                    onChange={(e) => handleSelectOperatore(fab.tipo, e.target.value)}
                    className="w-full h-10 rounded-lg border border-border/60 bg-background px-3 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500/60"
                    data-testid={`select-operatore-${idx}`}
                  >
                    <option value="">Seleziona {fab.ruolo}...</option>
                    {operatoriDisponibili.map((op) => (
                      <option key={op.id} value={op.id}>{op.nome} — Carico: {op.percentuale}%</option>
                    ))}
                  </select>
                </div>

                {/* Mini calendario slot */}
                {selectedOp && (
                  <div>
                    <label className="text-xs font-semibold text-foreground mb-2 block">Aggiungi slot settimanali</label>
                    <div className="border border-border/40 rounded-lg overflow-hidden">
                      {/* Header giorni */}
                      <div className="grid grid-cols-5 bg-muted/30 dark:bg-slate-800/50">
                        {GIORNI.map((g) => (
                          <div key={g} className="text-center py-2 text-[10px] font-semibold text-muted-foreground border-r border-border/20 last:border-r-0">
                            {g}
                          </div>
                        ))}
                      </div>
                      {/* Righe orarie */}
                      {SLOT_ORARI.map((ora) => (
                        <div key={ora} className="grid grid-cols-5 border-t border-border/20">
                          {GIORNI.map((giorno) => {
                            const isSelected = slots.some((s) => s.giorno === giorno && s.ora === ora);
                            const occupied = isSlotOccupied(selectedOp, giorno, ora);
                            return (
                              <button
                                key={`${giorno}-${ora}`}
                                disabled={occupied}
                                onClick={() => !occupied && (isSelected ? removeSlot(fab.tipo, giorno, ora) : addSlot(fab.tipo, giorno, ora))}
                                className={`py-1.5 text-[10px] font-medium border-r border-border/20 last:border-r-0 transition-all ${
                                  occupied
                                    ? 'bg-slate-200 dark:bg-slate-700/60 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50 line-through'
                                    : isSelected
                                      ? 'bg-violet-500 text-white'
                                      : 'hover:bg-violet-50 dark:hover:bg-violet-950/20 text-muted-foreground'
                                }`}
                                title={occupied ? 'Slot occupato' : ''}
                                data-testid={`slot-${idx}-${giorno}-${ora.replace(':', '')}`}
                              >
                                {ora}
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chips slot selezionati */}
                {slots.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">{slots.length} slot pianificati</p>
                    <div className="flex flex-wrap gap-1.5">
                      {slots
                        .sort((a, b) => GIORNI.indexOf(a.giorno) - GIORNI.indexOf(b.giorno) || a.ora.localeCompare(b.ora))
                        .map((s) => (
                        <span
                          key={`${s.giorno}-${s.ora}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-[10px] font-semibold"
                        >
                          {s.giorno} {s.ora}
                          <button
                            onClick={() => removeSlot(fab.tipo, s.giorno, s.ora)}
                            className="w-3.5 h-3.5 rounded-full hover:bg-violet-200 dark:hover:bg-violet-800 flex items-center justify-center ml-0.5"
                            data-testid={`remove-slot-${idx}-${s.giorno}-${s.ora.replace(':', '')}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Azioni */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1 h-11 rounded-xl gap-2" data-testid="step2b-indietro">
          <ChevronLeft className="w-4 h-4" /> Indietro
        </Button>
        <Button
          disabled={totalAssigned === 0 || totalSlots === 0}
          onClick={onConfirm}
          className="flex-1 h-11 rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="conferma-assegnazione"
        >
          <CheckCircle2 className="w-4 h-4" /> Conferma Pianificazione
        </Button>
      </div>
    </div>
  );
}

/* ──────────── DRAWER PRINCIPALE ──────────── */
export default function AssegnazioneProviderDrawer({ open, onClose, patient, onConfirm }) {
  const [wizardStep, setWizardStep] = useState(1);
  const [validated, setValidated] = useState(false);
  const [gestioneTipo, setGestioneTipo] = useState(null);

  /* Reset on open */
  useEffect(() => {
    if (open) {
      setWizardStep(1);
      setValidated(false);
      setGestioneTipo(null);
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const stepLabel = wizardStep === 1
    ? 'Validazione e Scelta'
    : gestioneTipo === 'esterna' ? 'Gestione Esterna' : 'Gestione Interna';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end"
          data-testid="assegnazione-drawer-overlay"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

          {/* Drawer panel */}
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full max-w-lg bg-card border-l border-border shadow-2xl flex flex-col h-full"
            data-testid="assegnazione-drawer-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border/40">
              <div>
                <h2 className="text-base font-semibold text-foreground">Assegnazione Erogatore</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px]">Step {wizardStep}/2</Badge>
                  <span className="text-[11px] text-muted-foreground">{stepLabel}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-muted/40 flex items-center justify-center transition-colors"
                data-testid="drawer-close"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex gap-2 px-5 py-3 border-b border-border/20">
              <div className={`flex-1 h-1 rounded-full transition-all ${wizardStep >= 1 ? 'bg-violet-500' : 'bg-muted/40'}`} />
              <div className={`flex-1 h-1 rounded-full transition-all ${wizardStep >= 2 ? 'bg-violet-500' : 'bg-muted/40'}`} />
            </div>

            {/* Content — scrollable */}
            <div className="flex-1 overflow-y-auto p-5">
              <AnimatePresence mode="wait">
                {wizardStep === 1 && (
                  <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <Step1
                      patient={patient}
                      validated={validated}
                      setValidated={setValidated}
                      gestioneTipo={gestioneTipo}
                      setGestioneTipo={setGestioneTipo}
                      onNext={() => setWizardStep(2)}
                    />
                  </motion.div>
                )}
                {wizardStep === 2 && gestioneTipo === 'esterna' && (
                  <motion.div key="step2a" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <Step2Esterna onConfirm={handleConfirm} onBack={() => setWizardStep(1)} />
                  </motion.div>
                )}
                {wizardStep === 2 && gestioneTipo === 'interna' && (
                  <motion.div key="step2b" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                    <Step2Interna onConfirm={handleConfirm} onBack={() => setWizardStep(1)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
